import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../patients/utils/patientUtils';

interface PatientAllergy {
  id: string;
  patient_id: string;
  allergy_name: string;
  reaction?: string;
  severity: string;
  onset_date?: string;
  notes?: string;
  verified_by?: string | null;
  verified_by_name?: string | null;
  is_verified: boolean;
  status: string;
  consultation_id?: string | null;
}

interface FormDataState {
  reaction: string;
  severity: string;
  onset_date: string;
  notes: string;
  status: string;
}

const EMPTY_FORM_DATA: FormDataState = {
  reaction: '',
  severity: '',
  onset_date: '',
  notes: '',
  status: 'Active'
};

interface AddAllergyModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patient?: { first_name: string; last_name: string; date_of_birth?: string } | null;
  onAllergyAdded: (allergy: PatientAllergy) => void;
  // Set when opened from an active consult - see AddDiagnosisModal for the
  // same pattern. Omit to add directly to a patient's file, no consult
  // required.
  consultationId?: string | null;
}

const AddAllergyModal: React.FC<AddAllergyModalProps> = ({ isOpen, onClose, patientId, patient, onAllergyAdded, consultationId }) => {
  const { token } = useAuth();
  const [allergyName, setAllergyName] = useState('');
  const [formData, setFormData] = useState<FormDataState>(EMPTY_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allergyName.trim()) {
      setError('Please enter an allergy');
      return;
    }

    if (!formData.reaction.trim()) {
      setError('Please describe the allergic reaction');
      return;
    }

    if (!formData.severity) {
      setError('Please select severity level');
      return;
    }

    if (!token) return;
    setIsSubmitting(true);
    setError('');

    try {
      const payload: Record<string, unknown> = {
        patient_id: patientId,
        allergy_name: allergyName.trim(),
        reaction: formData.reaction,
        severity: formData.severity,
        notes: formData.notes,
        status: formData.status,
        consultation_id: consultationId || null,
      };

      if (formData.onset_date.trim()) {
        payload.onset_date = formData.onset_date;
      }

      const response = await apiCall('/clinical/patients/allergies/create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }, token);

      if (response.ok) {
        const newAllergy = await response.json();
        onAllergyAdded(newAllergy);
        handleClose();
      } else {
        const errorData = await response.json();
        console.error('Backend error response:', errorData);
        const errorMsg = errorData.detail || errorData.message || JSON.stringify(errorData) || 'Failed to add allergy';
        setError(errorMsg);
      }
    } catch (error) {
      console.error('Error adding allergy:', error);
      setError(`Failed to add allergy: ${error instanceof Error ? error.message : error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAllergyName('');
    setFormData(EMPTY_FORM_DATA);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-base-300">
          <h2 className="text-xl font-semibold text-base-content">
            Add New Allergy for {patient ? `${patient.first_name} ${patient.last_name}` : 'Patient'}
            {patient?.date_of_birth && ` (DOB: ${formatDate(patient.date_of_birth)})`}
          </h2>
          <button
            onClick={handleClose}
            className="text-base-content opacity-60 hover:opacity-100 transition-colors"
          >
            <FaTimes className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {/* Allergy Name */}
          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Allergy *
            </label>
            <input
              type="text"
              value={allergyName}
              onChange={(e) => setAllergyName(e.target.value)}
              className="input input-bordered w-full"
              placeholder="Enter the patient's allergy here..."
              required
            />
          </div>

          {/* Reaction Description */}
          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Allergic Reaction *
            </label>
            <textarea
              value={formData.reaction}
              onChange={(e) => setFormData({ ...formData, reaction: e.target.value })}
              rows={2}
              className="textarea textarea-bordered w-full"
              placeholder="Describe the allergic reaction (e.g., rash, swelling, difficulty breathing)..."
              required
            />
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Severity *
              </label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="select select-bordered w-full"
                required
              >
                <option value="">Select severity...</option>
                <option value="Mild">Mild</option>
                <option value="Moderate">Moderate</option>
                <option value="Severe">Severe</option>
                <option value="Life-threatening">Life-threatening</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Onset Date
              </label>
              <input
                type="date"
                value={formData.onset_date}
                onChange={(e) => setFormData({ ...formData, onset_date: e.target.value })}
                className="input input-bordered w-full"
                placeholder="dd/mm/yyyy"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-base-content mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="select select-bordered w-full"
              >
                <option value="Active">Active</option>
                <option value="Resolved">Resolved</option>
                <option value="Suspected">Suspected</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-base-content mb-2">
              Additional Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={1}
              className="textarea textarea-bordered w-full"
              placeholder="Additional notes about this allergy..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-ghost flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !allergyName.trim() || !formData.reaction || !formData.severity}
              className="btn btn-primary flex-1"
            >
              {isSubmitting ? 'Adding...' : 'Add Allergy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAllergyModal;
